import { Component, ElementRef, EventEmitter, Input, Output, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { NgClass } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import { CameraIconComponent } from '../../../../../@icons/camera-icon.component';
import { CloseIconComponent } from '../../../../../@icons/close-icon.component';
import { LockIconComponent } from '../../../../../@icons/lock-icon.component';
import { SendIconComponent } from '../../../../../@icons/send-icon.component';
import {
  validateWillyPhoto,
  WILLY_PHOTO_MAX_COUNT,
  WillyPhotoError,
} from '../willy-photo.util';

export interface ChatMessageSubmit {
  text: string;
  photos?: File[];
}

interface PhotoPreview {
  file: File;
  url: string;
}

@Component({
    selector: 'vw-ai-chatbot-input-message',
    imports: [
        FormsModule,
        NgClass,
        TranslatePipe,
        CameraIconComponent,
        CloseIconComponent,
        LockIconComponent,
        SendIconComponent,
    ],
    templateUrl: './ai-chatbot-input-message.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrl: './ai-chatbot-input-message.component.scss'
})
export class AiChatbotInputMessageComponent {
  /** Hors réseau ou quota du jour épuisé : rien ne peut partir. */
  @Input() disabled = false;
  /** Texte du champ vide (clé i18n) ; il dit pourquoi quand la saisie est fermée. */
  @Input() placeholderKey = 'willy.placeholder';
  /**
   * Le diagnostic photo offert a servi (CR-05) : le bouton photo ouvre l'offre
   * au lieu du sélecteur natif.
   */
  @Input() photoLocked = false;

  @Output() messageSubmitted = new EventEmitter<ChatMessageSubmit>();
  @Output() photoLockedClick = new EventEmitter<void>();
  @Output() inputFocus = new EventEmitter<void>();
  @Output() inputBlur = new EventEmitter<void>();

  @ViewChild('photoInput') photoInput: ElementRef<HTMLInputElement>;

  message: string = '';
  photos: PhotoPreview[] = [];
  photoError: WillyPhotoError | null = null;

  resetMessage() {
    this.message = '';
    this.removeAllPhotos();
  }

  /** Pre-fill the input (e.g. "Diagnose this plant" entry point). */
  setMessage(text: string) {
    this.message = text;
  }

  /** Il y a de quoi envoyer : le bouton passe en vert plein. */
  get canSend(): boolean {
    return !this.disabled && (this.message.trim().length > 0 || this.photos.length > 0);
  }

  sendMessage() {
    if (this.disabled) return;
    if (this.message.length > 0 || this.photos.length > 0) {
      this.messageSubmitted.emit({
        text: this.message,
        photos: this.photos.length ? this.photos.map((p) => p.file) : undefined,
      });
      // The File references stay valid for the upload; only local state is cleared
      this.message = '';
      this.photos.forEach((p) => URL.revokeObjectURL(p.url));
      this.photos = [];
      this.photoError = null;
    }
  }

  onEnter() {
    this.sendMessage();
  }

  onSendButtonClick() {
    this.sendMessage();
  }

  onPhotoButtonClick() {
    if (this.photoLocked) {
      this.photoLockedClick.emit();
      return;
    }
    // Le sélecteur natif (feuille iOS, `setOnShowFileSelector` de la coque
    // Android) propose déjà l'appareil photo ou la photothèque.
    this.photoInput.nativeElement.click();
  }

  onPhotoSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files ?? []);
    // Allow re-selecting the same file after a remove
    input.value = '';
    this.photoError = null;
    for (const file of files) {
      if (this.photos.length >= WILLY_PHOTO_MAX_COUNT) {
        this.photoError = 'too-many';
        break;
      }
      const error = validateWillyPhoto(file);
      if (error) {
        this.photoError = error;
        continue;
      }
      this.photos.push({ file, url: URL.createObjectURL(file) });
    }
  }

  removePhoto(index: number) {
    const [removed] = this.photos.splice(index, 1);
    if (removed) {
      URL.revokeObjectURL(removed.url);
    }
    this.photoError = null;
  }

  private removeAllPhotos() {
    this.photos.forEach((p) => URL.revokeObjectURL(p.url));
    this.photos = [];
    this.photoError = null;
  }

  ngOnDestroy() {
    this.photos.forEach((p) => URL.revokeObjectURL(p.url));
  }

  onInputFocus() {
    this.inputFocus.emit();
  }

  onInputBlur() {
    this.inputBlur.emit();
  }
}
